import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { PhoneIcon, MailIcon, MapPinIcon, ArrowRightIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
const containerVariants = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.08
    }
  }
};
const itemVariants = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.4,
      ease: [0.25, 0.46, 0.45, 0.94]
    }
  }
};
export function ContactSection() {
  const {
    t
  } = useLanguage();
  return <footer className="w-full bg-black text-white" data-id="element-84">
      {/* Main footer content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12 sm:py-16 lg:py-20" data-id="element-85">
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12" data-id="element-86">
          {/* Brand */}
          <motion.div variants={itemVariants} className="lg:col-span-2" data-id="element-87">
            <div className="mb-4 sm:mb-5" data-id="element-88">
              <h2 className="text-xl sm:text-2xl font-semibold tracking-tight text-white" data-id="element-89">
                HABITALUM
              </h2>
              <p className="text-neutral-500 text-xs sm:text-sm font-light mt-1" data-id="element-90">
                {t('contact.tagline')}
              </p>
            </div>
            <p className="text-neutral-400 leading-relaxed mb-5 max-w-md text-sm font-light" data-id="element-91">
              {t('contact.brand.desc')}
            </p>
            <motion.div className="inline-flex items-center gap-2 px-3 py-2 bg-neutral-900 rounded-lg text-xs border border-neutral-800" whileHover={{
            backgroundColor: '#1a1a1a'
          }} data-id="element-92">
              <MapPinIcon className="w-3.5 h-3.5 text-white" data-id="element-93" />
              <span className="font-normal text-neutral-300" data-id="element-94">
                {t('contact.barcelona')}
              </span>
            </motion.div>
          </motion.div>

          {/* Contact - Construction */}
          <motion.div variants={itemVariants} data-id="element-95">
            <h3 className="font-medium text-sm sm:text-base mb-4 text-white flex items-center gap-2" data-id="element-96">
              <span className="w-5 h-px bg-white/30 rounded-full" data-id="element-97"></span>
              {t('contact.construction')}
            </h3>
            <ul className="space-y-3" data-id="element-98">
              <li data-id="element-99">
                <a href="mailto:construcciones@habitalum.es" className="group flex items-center gap-2 text-neutral-400 hover:text-white transition-colors text-sm" data-id="element-100">
                  <div className="w-7 h-7 rounded-full bg-neutral-900 flex items-center justify-center group-hover:bg-white group-hover:text-black transition-all" data-id="element-101">
                    <MailIcon className="w-3.5 h-3.5" data-id="element-102" />
                  </div>
                  <span className="font-light text-xs" data-id="element-103">
                    construcciones@habitalum.es
                  </span>
                </a>
              </li>
              <li className="text-neutral-500 text-xs leading-relaxed border-l border-neutral-800 pl-3 font-light" data-id="element-104">
                {t('contact.construction.desc')}
              </li>
            </ul>
          </motion.div>

          {/* Contact - Renovations */}
          <motion.div variants={itemVariants} data-id="element-105">
            <h3 className="font-medium text-sm sm:text-base mb-4 text-white flex items-center gap-2" data-id="element-106">
              <span className="w-5 h-px bg-white/30 rounded-full" data-id="element-107"></span>
              {t('contact.renovations')}
            </h3>
            <ul className="space-y-3" data-id="element-108">
              <li data-id="element-109">
                <a href="mailto:reformas@habitalum.es" className="group flex items-center gap-2 text-neutral-400 hover:text-white transition-colors text-sm" data-id="element-110">
                  <div className="w-7 h-7 rounded-full bg-neutral-900 flex items-center justify-center group-hover:bg-white group-hover:text-black transition-all" data-id="element-111">
                    <MailIcon className="w-3.5 h-3.5" data-id="element-112" />
                  </div>
                  <span className="font-light text-xs" data-id="element-113">
                    reformas@habitalum.es
                  </span>
                </a>
              </li>
              <li className="text-neutral-500 text-xs leading-relaxed border-l border-neutral-800 pl-3 font-light" data-id="element-114">
                {t('contact.renovations.desc')}
              </li>
            </ul>
          </motion.div>
        </motion.div>

        {/* Phone CTA */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.4,
        delay: 0.2
      }} className="mt-10 sm:mt-14 pt-8 border-t border-neutral-900" data-id="element-115">
          <div className="flex flex-col md:flex-row items-center justify-between gap-5 bg-neutral-900/50 p-5 sm:p-6 rounded-xl border border-neutral-800" data-id="element-116">
            <div className="text-center md:text-left" data-id="element-117">
              <p className="text-neutral-400 mb-1 font-normal text-xs sm:text-sm" data-id="element-118">
                {t('contact.call.question')}
              </p>
              <motion.a href="tel:+34631012592" className="inline-flex items-center gap-2 text-xl sm:text-2xl font-normal hover:text-neutral-300 transition-colors" whileHover={{
              x: 2
            }} data-id="element-119">
                <PhoneIcon className="w-5 h-5 sm:w-6 sm:h-6" data-id="element-120" />
                +34 631 01 25 92
              </motion.a>
            </div>
            <Link to="/construccion" data-id="element-121">
              <motion.span className="inline-flex items-center gap-2 px-5 sm:px-6 py-2.5 sm:py-3 bg-white text-black font-medium rounded-full hover:bg-neutral-200 transition-colors text-sm" whileHover={{
              scale: 1.02
            }} whileTap={{
              scale: 0.98
            }} data-id="element-122">
                {t('contact.cta')}
                <ArrowRightIcon className="w-4 h-4" data-id="element-123" />
              </motion.span>
            </Link>
          </div>
        </motion.div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-neutral-900 bg-black" data-id="element-124">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-5 sm:py-6" data-id="element-125">
          <div className="flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-neutral-500" data-id="element-126">
            <p className="text-center md:text-left font-light" data-id="element-127">
              © {new Date().getFullYear()}{' '}
              <span className="font-medium text-white" data-id="element-128">Habitalum</span>.{' '}
              {t('contact.rights')}
            </p>
            <div className="flex gap-5 sm:gap-6" data-id="element-129">
              <a href="#" className="hover:text-white transition-colors font-light" data-id="element-130">
                {t('contact.privacy')}
              </a>
              <a href="#" className="hover:text-white transition-colors font-light" data-id="element-131">
                {t('contact.legal')}
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>;
}